const SELECTORS = {
  TEMPLATES: {
    ADDRESS_LIST_ITEM: "#address-list-item-template",
    EMPTY_STATE: "#address-list-empty-template",
    ERROR_STATE: "#address-list-error-template"
  },
  ADDRESS_LIST_ITEM: ".js-local-delivery-address-list-item",
  ADDRESS_STREET: ".js-address-street",
  ADDRESS_CITY_STATE: ".js-address-city-state"
};

const CSS_CLASSES = {
  SELECTED: "local-delivery-address-list__item--selected"
};

/**
 * AddressListRenderer
 *
 * Handles HTML rendering for address list items using templates from the DOM
 */
export default class AddressListRenderer {
  /**
   * Gets the address list template
   * @returns {Element} The address list template
   * @private
   */
  static get #addressListTemplate() {
    return document.querySelector(SELECTORS.TEMPLATES.ADDRESS_LIST_ITEM);
  }

  /**
   * Gets the address list empty template
   * @returns {Element} The address list empty template
   * @private
   */
  static get #addressListEmptyTemplate() {
    return document.querySelector(SELECTORS.TEMPLATES.EMPTY_STATE);
  }

  /**
   * Gets the address list error template
   * @returns {Element} The address list error template
   * @private
   */
  static get #addressListErrorTemplate() {
    return document.querySelector(SELECTORS.TEMPLATES.ERROR_STATE);
  }

  /**
   * Converts a DocumentFragment or Node to HTML string
   * @param {DocumentFragment|Node} node - Node to convert
   * @returns {string} HTML string
   */
  static #nodeToHtmlString(node) {
    const wrapper = document.createElement("div");
    wrapper.appendChild(node);
    return wrapper.innerHTML;
  }

  /**
   * Creates HTML for a single address item using the template
   * @param {Object} address - Address object
   * @param {boolean} isSelected - Whether this address is selected
   * @returns {string} HTML string
   */
  static createAddressItemHtml(address, isSelected) {
    const $template = AddressListRenderer.#addressListTemplate;

    if (!$template) return "";

    const $clone = $template.content.cloneNode(true);
    const $item = $clone.querySelector(SELECTORS.ADDRESS_LIST_ITEM);

    // Set address ID and selected state
    $item.setAttribute("data-address-id", address.id);

    isSelected && $item.classList.add(CSS_CLASSES.SELECTED);

    // Populate text slots
    const street = address.address1 || "";
    const cityStateZip = AddressListRenderer.formatCityStateZip(address);

    const $streetSlot = $clone.querySelector(SELECTORS.ADDRESS_STREET);
    const $cityStateZipSlot = $clone.querySelector(SELECTORS.ADDRESS_CITY_STATE);

    if ($streetSlot) $streetSlot.textContent = street;
    if ($cityStateZipSlot) $cityStateZipSlot.textContent = cityStateZip;

    return AddressListRenderer.#nodeToHtmlString($clone);
  }

  /**
   * Renders empty state HTML using the template
   * @returns {string} HTML string
   */
  static renderEmptyState() {
    const $template = AddressListRenderer.#addressListEmptyTemplate;

    if (!$template) return "";

    return AddressListRenderer.#nodeToHtmlString($template.content.cloneNode(true));
  }

  /**
   * Renders error state HTML using the template
   * @returns {string} HTML string
   */
  static renderErrorState() {
    const $template = AddressListRenderer.#addressListErrorTemplate;

    if (!$template) return "";

    return AddressListRenderer.#nodeToHtmlString($template.content.cloneNode(true));
  }

  /**
   * Formats city, state, and zipcode into a single string
   * @param {Object} address - Address object
   * @returns {string} Formatted string
   */
  static formatCityStateZip(address) {
    const parts = [];

    if (address.city) parts.push(address.city);
    if (address.state) parts.push(address.state);
    if (address.zipcode) parts.push(address.zipcode);
    if (address.country) parts.push(address.country);

    return parts.filter(Boolean).join(", ");
  }
}
